import { baseUrl, endpoints } from "../../config/apiEndpoint";
import { authUtils } from "../../util/AuthUtils";
import { apiClient } from "../shared/apiClient";

/**
 * Register a new user.
 *
 * @param {string} email - The email for register.
 * @param {string} password - The password for register.
 * @param {string} name - The name for register.
 * @param {string} mobile - The mobile for register.
 * @param {string} flatNo - The flat/house number.
 * @param {object} selectedLocation - The selected location object.
 * @return {Promise<object|string>} A promise resolving with user data or error code.
 */
export async function registerUser(
  email,
  password,
  name,
  mobile,
  flatNo,
  selectedLocation
) {
  try {
    const response = await apiClient.post(
      endpoints.auth.register,
      {
        email,
        password,
        name,
        phoneNumber: mobile,
        flatNo,
        selectedLocation
      },
      {},
      false
    );

    const data = response.data;

    if (!data.accessToken) {
      // If backend sends conflict (email already exists)
      if (data.status === 409 || data.message?.includes("EMAIL_EXISTS")) {
        return "EMAIL_EXISTS";
      }
      throw new Error(data.message || "Registration failed.");
    }

    // Store tokens
    authUtils.storeTokens(
      data.accessToken,
      data.refreshToken,
      selectedLocation
    );

    let profile = {};
    try {
      profile = await getUserProfile();
      authUtils.storeUserProfile(profile.user);
    } catch (profileError) {
      console.warn("Failed to fetch user profile:", profileError);
    }

    return {
      uid: profile.user?.userId || null,
      email,
      name,
      mobile,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      message: data.message,
      success: true
    };
  } catch (error) {
    if (error.message.includes("EMAIL_EXISTS")) {
      return "EMAIL_EXISTS";
    }
    throw new Error("Registration failed.");
  }
}

/**
 * Login user with email and password.
 *
 * @param {string} email - The email for login.
 * @param {string} password - The password for login.
 * @return {Promise<object|string>} A promise that resolves with
 * user data or an error code.
 */
export async function loginUser(email, password) {
  try {
    const result = await apiClient.post(
      endpoints.auth.login,
      {
        email,
        password
      },
      {},
      false
    ); // 👈 false = no token needed for login

    // Handle error responses
    if (result.error) {
      return result.error;
    }

    // Store tokens
    if (result?.data?.accessToken) {
      authUtils.storeTokens(result.data.accessToken, result.data.refreshToken);
    }

    let profile = {};
    try {
      profile = await getUserProfile();
      authUtils.storeUserProfile(profile.user);
      console.log("User profile fetched:", profile.user);
    } catch (profileError) {
      console.warn("Failed to fetch user profile:", profileError);
    }

    // Return consistent user object
    return {
      uid: profile.user?.userId || profile.userId,
      email,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      message: result.message,
      profile: profile.user || profile,
      success: true
    };
  } catch (error) {
    // Custom error mapping
    if (error.message.includes("EMAIL_NOT_FOUND")) {
      return "EMAIL_NOT_FOUND";
    }
    if (error.message.includes("TOO_MANY_REQUESTS")) {
      return "TOO_MANY_REQUESTS";
    }
    if (error.message.includes("INVALID_CREDENTIALS")) {
      return "INVALID_CREDENTIALS";
    }

    throw new Error(
      "Login failed. Please check your email and password and try again."
    );
  }
}

const getUserProfile = async () => {
  try {
    const response = await apiClient.get(endpoints.user.profile, {}, true);

    if (!response.success) {
      const errorData = response.error;
      throw new Error(errorData.message || "Failed to fetch user profile");
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

/**
 * Logout user.
 *
 * @return {Promise<{success: boolean, message: string}>}
 */
export async function logoutUser() {
  try {
    const accessToken = authUtils.getAccessToken();
    const refreshToken = authUtils.getRefreshToken();

    // If no tokens exist, user is already logged out
    if (!accessToken && !refreshToken) {
      authUtils.clearStoredTokens();

      return {
        success: true,
        message: "Already logged out"
      };
    }

    // If logout API exists, try calling it
    if (endpoints.auth.logout) {
      try {
        const response = await fetch(`${baseUrl}${endpoints.auth.logout}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Logout API response:", data);
        } else {
          console.warn("Logout API call failed, proceeding with local cleanup");
        }
      } catch (apiError) {
        console.warn("Logout API call error:", apiError);
      }
    }

    // Always clear tokens locally
    authUtils.clearStoredTokens();

    return {
      success: true,
      message: "Logged out successfully"
    };
  } catch (error) {
    console.error("Logout error:", error);

    // Cleanup anyway
    authUtils.clearStoredTokens();

    return {
      success: true,
      message: "Logged out with cleanup"
    };
  }
}

/**
 * Save customer address.
 *
 * @param {object} address - The address object to be saved.
 * @return {Promise<object>} A promise resolving with API response.
 */
export async function saveAddress(address) {
  try {
    const payload = {
      line1: address.addressLine1,
      line2: address.addressLine2 ?? "",
      city: address.city,
      state: address.state,
      zipCode: address.pinCode,
      country: address.country ?? "India",
      latitude: address.latitude,
      longitude: address.longitude,
      locationId: address.locationId
    };

    const response = await apiClient.post(
      `${endpoints.addresses.add}`,
      payload,
      true
    );

    return response;
  } catch (error) {
    console.error("Save address failed:", error);
    throw error;
  }
}

/**
 * Reset user password.
 *
 * @param {string} email - The email for password reset.
 * @return {Promise<string>} A promise resolving with reset status.
 */
export async function resetUserPassword(email) {
  try {
    // For now, this is mocked. Replace with API call when available.
    const status = "RESET_EMAIL_SEND";
    // Other possible statuses:
    // const status = 'RESET_EMAIL_USER_NOT_FOUND';
    // const status = 'RESET_EMAIL_FAILED';

    return status;
  } catch (error) {
    console.error("Password reset failed:", error);
    throw new Error("reset failed");
  }
}

/**
 * get add address
 *
 * @return {Promise} A promise that resolves with products.
 */
export async function fetchAddresses() {
  try {
    return await apiClient.get(endpoints.user.addresses, {}, true);
  } catch (error) {
    console.error("Error fetching user addresses:", error);
    throw error;
  }
}
